<script setup>
// Manutenções do provedor ("está caindo só para mim?"). Porta da AbaManutencao
// do eryx-sgp. Não depende do contato. Cadastrar publica: vira a mensagem que os
// clientes leem na central do assinante.
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import messages from './i18n';
import { erroDaApi } from './api';
import { momento } from './format';
import SgpSelo from './SgpSelo.vue';

const props = defineProps({
  api: { type: Object, required: true },
});

const { t } = useI18n({ messages });

const TOM_DA_FASE = {
  agora: 'atencao',
  programada: 'aberto',
  encerrada: 'neutro',
  inativa: 'neutro',
};

const manutencoes = ref(null);
const carregando = ref(false);
const erro = ref('');
const aviso = ref('');
const finalizando = ref(null);

const formularioAberto = ref(false);
const pops = ref(null);
const cadastrando = ref(false);
const erroCadastro = ref('');
const novo = reactive({
  descricao: '',
  inicio: '',
  fim: '',
  status: 1,
  mensagem_central: '',
  pops: [],
});

const campo =
  'w-full px-2 py-1 mb-0 text-sm rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12';

const busca = async () => {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await props.api.manutencoes();
    manutencoes.value = data.manutencoes;
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_SGP.ERRO_GENERICO'));
  } finally {
    carregando.value = false;
  }
};

// O servidor confere na listagem se ela saiu mesmo do ar antes de dizer que sim.
const finaliza = async manutencao => {
  finalizando.value = manutencao.id;
  erro.value = '';
  aviso.value = '';
  try {
    await props.api.finalizaManutencao(manutencao.id);
    aviso.value = t('NEUXS_SGP.MANUTENCAO.FINALIZADA');
    await busca();
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_SGP.ERRO_GENERICO'));
  } finally {
    finalizando.value = null;
  }
};

// Os POPs só são buscados quando o formulário abre: a maioria dos atendimentos
// nunca chega aqui.
const abreFormulario = async () => {
  formularioAberto.value = true;
  erroCadastro.value = '';
  if (pops.value) return;
  try {
    const { data } = await props.api.pops();
    pops.value = data.pops;
  } catch (error) {
    erroCadastro.value = erroDaApi(error, t('NEUXS_SGP.ERRO_GENERICO'));
  }
};

const cadastra = async () => {
  cadastrando.value = true;
  erroCadastro.value = '';
  try {
    await props.api.criaManutencao({ ...novo });
    Object.assign(novo, {
      descricao: '',
      inicio: '',
      fim: '',
      status: 1,
      mensagem_central: '',
      pops: [],
    });
    formularioAberto.value = false;
    aviso.value = t('NEUXS_SGP.MANUTENCAO.CADASTRADA');
    await busca();
  } catch (error) {
    erroCadastro.value = erroDaApi(error, t('NEUXS_SGP.ERRO_GENERICO'));
  } finally {
    cadastrando.value = false;
  }
};

onMounted(busca);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center gap-1">
      <Button
        v-if="!formularioAberto"
        :label="t('NEUXS_SGP.MANUTENCAO.NOVA')"
        icon="i-lucide-plus"
        size="xs"
        variant="faded"
        @click="abreFormulario"
      />
      <Button
        class="ml-auto"
        icon="i-lucide-refresh-cw"
        size="xs"
        variant="ghost"
        color="slate"
        :is-loading="carregando"
        :title="t('NEUXS_SGP.ATUALIZAR')"
        @click="busca"
      />
    </div>

    <form
      v-if="formularioAberto"
      class="flex flex-col gap-2 p-2 rounded-lg outline outline-1 outline-n-weak"
      @submit.prevent="cadastra"
    >
      <p class="text-xs text-n-amber-11">
        {{ t('NEUXS_SGP.MANUTENCAO.AVISO') }}
      </p>
      <label class="flex flex-col gap-1 text-xs">
        {{ t('NEUXS_SGP.MANUTENCAO.DESCRICAO') }}
        <input v-model="novo.descricao" type="text" :class="campo" />
      </label>
      <div class="grid grid-cols-2 gap-2">
        <label class="flex flex-col gap-1 text-xs">
          {{ t('NEUXS_SGP.MANUTENCAO.INICIO') }}
          <input v-model="novo.inicio" type="datetime-local" :class="campo" />
        </label>
        <label class="flex flex-col gap-1 text-xs">
          {{ t('NEUXS_SGP.MANUTENCAO.FIM') }}
          <input v-model="novo.fim" type="datetime-local" :class="campo" />
        </label>
      </div>
      <label class="flex flex-col gap-1 text-xs">
        {{ t('NEUXS_SGP.MANUTENCAO.STATUS') }}
        <select v-model.number="novo.status" :class="campo">
          <option
            v-for="codigo in [0, 1, 2, 3, 4]"
            :key="codigo"
            :value="codigo"
          >
            {{ t(`NEUXS_SGP.MANUTENCAO.STATUS_OPCOES.${codigo}`) }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1 text-xs">
        {{ t('NEUXS_SGP.MANUTENCAO.MENSAGEM') }}
        <textarea v-model="novo.mensagem_central" rows="2" :class="campo" />
      </label>
      <fieldset class="flex flex-col gap-1 text-xs">
        <legend class="mb-1">{{ t('NEUXS_SGP.MANUTENCAO.POPS') }}</legend>
        <Spinner v-if="!pops && !erroCadastro" :size="12" />
        <div class="flex flex-col gap-1 overflow-y-auto max-h-40">
          <label
            v-for="pop in pops || []"
            :key="pop.id"
            class="flex items-center gap-2"
          >
            <input v-model="novo.pops" type="checkbox" :value="pop.id" />
            {{ pop.nome }}
          </label>
        </div>
      </fieldset>
      <p v-if="erroCadastro" class="text-n-ruby-11">{{ erroCadastro }}</p>
      <div class="flex justify-end gap-2">
        <Button
          :label="t('NEUXS_SGP.MANUTENCAO.CANCELAR')"
          size="xs"
          variant="ghost"
          color="slate"
          type="button"
          @click="formularioAberto = false"
        />
        <Button
          :label="t('NEUXS_SGP.MANUTENCAO.CADASTRAR')"
          size="xs"
          type="submit"
          :is-loading="cadastrando"
        />
      </div>
    </form>

    <p v-if="aviso" class="text-n-teal-11">{{ aviso }}</p>
    <p v-if="erro" class="text-n-ruby-11">{{ erro }}</p>

    <div
      v-if="carregando && !manutencoes"
      class="flex items-center gap-2 text-n-slate-11"
    >
      <Spinner :size="14" />
      {{ t('NEUXS_SGP.CONSULTANDO') }}
    </div>
    <p
      v-else-if="manutencoes && manutencoes.length === 0"
      class="text-n-slate-11"
    >
      {{ t('NEUXS_SGP.MANUTENCAO.NENHUMA') }}
    </p>
    <ul v-else-if="manutencoes" class="flex flex-col gap-2">
      <li
        v-for="manutencao in manutencoes"
        :key="manutencao.id"
        class="flex flex-col gap-1 p-2 rounded-lg outline outline-1 outline-n-weak"
      >
        <div class="flex items-center justify-between gap-2">
          <SgpSelo
            :tom="TOM_DA_FASE[manutencao.fase]"
            :texto="t(`NEUXS_SGP.MANUTENCAO.FASE.${manutencao.fase}`)"
          />
          <span class="text-xs text-n-slate-11">{{ manutencao.status }}</span>
        </div>
        <strong class="text-sm">{{ manutencao.descricao }}</strong>
        <span class="text-xs text-n-slate-11">
          {{ `${momento(manutencao.inicio)} → ${momento(manutencao.fim)}` }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{
            manutencao.pops.length
              ? manutencao.pops.join(', ')
              : t('NEUXS_SGP.MANUTENCAO.TODOS_OS_POPS')
          }}
        </span>
        <p v-if="manutencao.mensagem_central" class="text-xs">
          {{ manutencao.mensagem_central }}
        </p>
        <Button
          v-if="manutencao.pode_finalizar"
          class="self-start"
          :label="t('NEUXS_SGP.MANUTENCAO.FINALIZAR')"
          icon="i-lucide-circle-check"
          size="xs"
          variant="faded"
          color="teal"
          :is-loading="finalizando === manutencao.id"
          @click="finaliza(manutencao)"
        />
      </li>
    </ul>
  </div>
</template>
